import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import map from 'lodash-es/map';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';

export namespace NToggle {
    /**
     * Group identifier
     */
    export type TGroupId = string | number;
    /**
     * Item id
     * first element is group id
     * second is item id within the group
     */
    export type TId = [TGroupId, string | number];

    /**
     * Open request options
     */
    export interface IOpenOptions {
        delay?: number;
    }
    /**
     * Close request options
     */
    export interface ICloseOptions {
        delay?: number;
    }

    export type EEventName = 'click' | 'hover';

    /**
     * Close options
     */
    export interface ICloseGroupOptions {
        /**
         * If its passed then we will only close if another item is open within the groul
         */
        exceptionItems?: (string | number)[];
    }
}

@Injectable({
    providedIn: 'root'
})
export class ToggleService {
    /**
     * Existing toggler groups
     */
    private groups: { [groupId: string]: ToggleGroup } = {};

    /**
     * Opens item
     */
    open(id: NToggle.TId, options?: NToggle.IOpenOptions): void {
        this.getGroup(id[0]).queue(() => this.getItem(id).open(), options?.delay);
    }

    /**
     * Closes item
     */
    close(id: NToggle.TId, options?: NToggle.ICloseOptions): void {
        if (this.isOpen(id)) {
            this.getGroup(id[0]).queue(() => this.getItem(id).close(), options?.delay);
        }
    }

    /**
     * Toggles item state
     */
    toggle(id: NToggle.TId): void {
        this.getItem(id).toggle();
    }

    /**
     * Checks if item is open
     */
    isOpen(id: NToggle.TId): boolean {
        return this.getItem(id).state.value;
    }

    /**
     * Close all group items
     */
    closeGroup(id: NToggle.TGroupId, options?: NToggle.ICloseGroupOptions): void {
        this.getGroup(id).close(options);
    }

    /**
     * Checks if any element of the group is open
     */
    isGroupOpen(groupId: NToggle.TGroupId): boolean {
        return this.getGroup(groupId).state.value;
    }

    /**
     * Gets item by its id
     * If does not exist it will be created
     */
    getItem(id: NToggle.TId): ToggleItem {
        const [groupId, itemId] = id;
        return this.getGroup(groupId).getItem(itemId);
    }

    /**
     * Gets all groups
     * @returns
     */
    getGroups() {
        return map(this.groups, g => g);
    }

    /**
     * Retrieves group by its string id
     * creates if doesnt exist
     */
    getGroup(groupId: NToggle.TGroupId | NToggle.TId): ToggleGroup {
        if (Array.isArray(groupId)) {
            groupId = groupId[0];
        }
        if (!this.groups[groupId]) {
            this.groups[groupId] = new ToggleGroup(groupId);
        }
        return this.groups[groupId];
    }

    /**
     * Gets state of group of element
     */
    getState(id: NToggle.TGroupId | NToggle.TId): BehaviorSubject<boolean> {
        if (typeof id === 'string' || typeof id === 'number') {
            return this.getGroup(id).state;
        }
        return this.getItem(id).state;
    }

    register(id: NToggle.TId) {
        this.getItem(id);
    }

    unregister(id: NToggle.TId) {
        this.getGroup(id[0]).removeItem(id[1]);
    }

    getHTMLId(id: any): string | undefined {
        if (typeof id === 'string' || typeof id === 'number') {
            return `toggle-${id}`;
        }
        if (Array.isArray(id)) {
            return `toggle-${id.join('::')}`;
        }
        return undefined;
    }
}

export class ToggleItem {
    readonly state = new BehaviorSubject<boolean>(false);

    constructor(readonly id: NToggle.TId) {}

    is(id: NToggle.TId) {
        return this.id[0] === id[0] && this.id[1] === id[1];
    }

    close(): void {
        if (this.state.value) {
            this.state.next(false);
        }
    }

    open(): void {
        if (!this.state.value) {
            this.state.next(true);
        }
    }

    toggle(): void {
        this.state.next(!this.state.value);
    }

    get isOpen(): boolean {
        return this.state.value;
    }
}

export class ToggleGroup {
    /**
     * Group open state
     */
    readonly state = new BehaviorSubject<boolean>(false);

    /**
     * Currently open one
     */
    readonly current = new BehaviorSubject<ToggleItem | undefined>(undefined);

    readonly register: { [id: string]: { item: ToggleItem; subscription: Subscription } } = {};

    @NgInject(TimeoutService)
    private timeoutService!: TimeoutService;

    private scheduledTimeout?: number;

    constructor(private readonly id: NToggle.TGroupId) {}

    close(options?: NToggle.ICloseGroupOptions) {
        const exceptions = options?.exceptionItems?.map(key => [this.id, key]) as NToggle.TId[];
        this.getItems().forEach(i => {
            if (!exceptions || !exceptions.find(exception => i.is(exception))) {
                i.close();
            }
        });
    }

    openAny() {
        this.getItems()[0]?.open();
    }

    get isOpen(): boolean {
        return this.state.value;
    }

    getItems() {
        return map(this.register, i => i.item);
    }

    queue(command: () => void, delay: number | undefined) {
        this.clearQueue();
        if (delay) {
            this.scheduledTimeout = this.timeoutService.setTimeout(command, delay);
        } else {
            command();
        }
    }

    getItem(itemId: string | number): ToggleItem {
        if (!this.register[itemId]) {
            const item = new ToggleItem([this.id, itemId]);
            const subscription = item.state.subscribe(open => {
                // close others if anyone gets open
                if (open) {
                    this.getItems()
                        .filter(i => i !== item && i.isOpen)
                        .forEach(i => i.close());
                }
                this.updateState();
            });
            this.register[itemId] = { item, subscription };
        }
        return this.register[itemId].item;
    }

    removeItem(itemId: string | number): void {
        const item = this.register[itemId];
        if (item) {
            item.subscription.unsubscribe();
            delete this.register[itemId];
            // if it was open then lets update state
            if (item.item.isOpen) {
                this.updateState();
            }
        }
    }

    /**
     * Reset any waiting process
     */
    private clearQueue() {
        if (this.scheduledTimeout) {
            clearTimeout(this.scheduledTimeout);
            this.scheduledTimeout = undefined;
        }
    }

    /**
     * Syncs grup state with items state
     */
    private updateState(): void {
        this.clearQueue();
        this.current.next(this.getItems().find(i => i.state.value));
        const state = !!this.current.value;

        if (this.state.value !== state) {
            this.state.next(state);
        }
    }
}
